// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useRef, useEffect, useCallback, useState } from "react";
import {
  Globe,
  Mail,
  SquarePen,
  Eye,
  Database,
  Plus,
  Trash2,
  Loader2,
  X,
} from "lucide-react";
import { workflowStatusService } from "../../services/workflowStatusService";
import toast from "react-hot-toast";
import { usePermissions } from "../../hooks/usePermissions";

import ApiConfigurationForm from "../../components/admin/WorkflowStatus/ApiConfigurationForm";
import EmailConfigurationForm from "../../components/admin/WorkflowStatus/EmailConfigurationForm";
import EmailTemplateEditor from "../../components/admin/WorkflowStatus/EmailTemplateEditor";
import ApiConfigurationCard from "../../components/admin/WorkflowStatus/ApiConfigurationCard";
import EmailConfigurationCard from "../../components/admin/WorkflowStatus/EmailConfigurationCard";
import OverviewTab from "../../components/admin/WorkflowStatus/OverviewTab";
import StatusCard from "../../components/admin/WorkflowStatus/StatusCard";
import Pagination from "../../components/admin/WorkflowStatus/Pagination";
import LoadingOverlay from "../../components/LoadingOverlay";
import InlineNotification from "../../components/InlineNotification";
import useInlineNotification from "../../hooks/useInlineNotification";
import EditStatusForm from "../../components/admin/WorkflowStatus/EditStatusForm";
import AddStatusForm from "../../components/admin/WorkflowStatus/AddStatusForm";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import useTenantBranding from "../../hooks/useTenantBranding";

const colorMap = {
  slate:
    "bg-slate-100 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-600",
  sky: "bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-200 border-sky-200 dark:border-sky-700",
  emerald:
    "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-700",
  green:
    "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700",
  blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700",
  rose: "bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200 border-rose-200 dark:border-rose-700",
  orange:
    "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-200 dark:border-orange-700",
  gray: "bg-gray-100 dark:bg-slate-700/50 text-gray-800 dark:text-slate-200 border-gray-200 dark:border-slate-600",
  violet:
    "bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200 border-violet-200 dark:border-violet-700",
  pink: "bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200 border-pink-200 dark:border-pink-700",
  purple:
    "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 border-purple-200 dark:border-purple-700",
};

const WorkflowStatus = () => {
  const [statuses, setStatuses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");
  const { hasPermission } = usePermissions();

  // Edit mode states
  const [editingApi, setEditingApi] = useState(null);
  const [editingEmail, setEditingEmail] = useState(null);
  const [isAddingApi, setIsAddingApi] = useState(false);
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Add missing state variables for edit form
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);

  // API configurations state - now fetched from API
  const [apiConfigs, setApiConfigs] = useState([]);
  const [apiConfigsLoading, setApiConfigsLoading] = useState(false);
  const [isDeleteFormOpen, setIsDeleteFormOpen] = useState(false);

  // Email configurations state - now fetched from API
  const [emailConfigs, setEmailConfigs] = useState([]);
  const [emailConfigsLoading, setEmailConfigsLoading] = useState(false);
  // Add new state for add form
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // New item templates - updated to match API structure
  const [newApi, setNewApi] = useState({
    call_name: "",
    endpoint_url: "",
    http_method: "POST",
    headers: {},
    payload_template: {},
    timeout_seconds: 30,
    retry_attempts: 3,
    is_enabled: true,
    execution_order: 0,
    is_blocking: false,
  });

  const [newEmail, setNewEmail] = useState({
    notification_name: "",
    template_code: "",
    recipient_type: "customer",
    custom_recipients: [],
    delay_hours: 0,
    is_enabled: true,
    send_immediately: true,
    execution_order: 0,
    conditions: {},
  });

  // Add notification hook
  const { notification, showSuccess, showError, hideNotification } =
    useInlineNotification(5000);

  const hasInitializedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const [templateEditorLoading, setTemplateEditorLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const templatesLoadedRef = useRef(false);

  const loadTemplatesForEditor = useCallback(async () => {
    if (templatesLoadedRef.current) {
      return templates;
    }

    templatesLoadedRef.current = true;
    setTemplatesLoading(true);

    try {
      const data = await workflowStatusService.getEmailTemplates({
        is_active: null,
        skip: 0,
        limit: 1000,
      });
      const templatesData = data.items || [];
      setTemplates(templatesData);
      return templatesData;
    } catch (error) {
      console.error("Error loading templates:", error);
      setTemplates([]);
      return [];
    } finally {
      setTemplatesLoading(false);
    }
  }, [templates]);

  const handleOpenTemplateEditor = useCallback(
    async (email) => {
      setTemplateEditorLoading(true);

      try {
        const templatesData = await loadTemplatesForEditor();
        const templateExists = templatesData.find(
          (t) => t.template_code === email.template_code,
        );
        if (templateExists) {
          await new Promise((resolve) => setTimeout(resolve, 500));

          setEditingTemplate({
            ...email,
            template_code: email.template_code || "",
            templateContent: email.templateContent || "",
          });
        } else {
          toast.error(`Template "${email.template_code}" nicht gefunden!`);
        }
      } catch (error) {
        console.error("Error opening template editor:", error);
        toast.error("Fehler beim Öffnen des Template-Editors");
      } finally {
        setTemplateEditorLoading(false);
      }
    },
    [loadTemplatesForEditor],
  );

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    abortControllerRef.current = new AbortController();

    const fetchInitialData = async () => {
      hasInitializedRef.current = true;
      setLoading(true);

      try {
        const data = await workflowStatusService.getWorkflowStatuses(
          {
            skip,
            limit,
          },
          { signal: abortControllerRef.current.signal },
        );

        if (data.items && data.items.length > 0) {
          const statusesWithCounts = data.items.map((status) => ({
            ...status,
            apiCount: status.api_calls_count || 0,
            emailCount: status.email_notifications_count || 0,
            actionCount: 0,
          }));
          setStatuses(statusesWithCounts);
          // Only select first item by default on desktop, not on mobile
          setSelected(!isMobile ? statusesWithCounts[0] || null : null);
          setTotal(data.total || 0);
        } else {
          setStatuses([]);
          setSelected(null);
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error fetching initial data:", error);
        setStatuses([]);
        setTotal(0);
        setSelected(null);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchApiCalls = useCallback(async () => {
    if (!selected?.status_id) return;

    setApiConfigsLoading(true);

    try {
      const data = await workflowStatusService.getWorkflowApiCalls(
        selected.status_id,
      );
      setApiConfigs(data.items || []);
    } catch (error) {
      console.error("Error fetching API calls:", error);
      setApiConfigs([]);
    } finally {
      setApiConfigsLoading(false);
    }
  }, [selected?.status_id]);

  const fetchEmailNotifications = useCallback(async () => {
    if (!selected?.status_id) return;

    setEmailConfigsLoading(true);

    try {
      const data = await workflowStatusService.getWorkflowEmailNotifications(
        selected.status_id,
      );
      setEmailConfigs(data.items || []);
    } catch (error) {
      console.error("Error fetching email notifications:", error);
      setEmailConfigs([]);
    } finally {
      setEmailConfigsLoading(false);
    }
  }, [selected?.status_id]);

  const handleTabClick = useCallback(
    (tabName) => {
      if (tabName === "api" && !hasPermission("api.access")) {
        return;
      }
      // Close any open editors/add forms when switching tabs
      setEditingApi(null);
      setIsAddingApi(false);
      setEditingEmail(null);
      setIsAddingEmail(false);
      setEditingTemplate(null);
      setActiveTab(tabName);

      if (tabName === "api" && selected?.status_id) {
        fetchApiCalls();
      } else if (tabName === "email" && selected?.status_id) {
        fetchEmailNotifications();
      }
    },
    [
      selected?.status_id,
      fetchApiCalls,
      fetchEmailNotifications,
      hasPermission,
    ],
  );

  const handleStatusSelect = useCallback((status) => {
    setSelected(status);
    setActiveTab("overview");
    setApiConfigs([]);
    setEmailConfigs([]);
    // Close any open editors/add forms when changing selected status
    setEditingApi(null);
    setIsAddingApi(false);
    setEditingEmail(null);
    setIsAddingEmail(false);
    setEditingTemplate(null);
  }, []);

  const page = Math.floor(skip / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  const handlePageChange = useCallback(
    (newPage) => {
      const newSkip = (newPage - 1) * limit;
      setSkip(newSkip);
    },
    [limit],
  );

  const handleItemsPerPageChange = useCallback((newLimit) => {
    setLimit(newLimit);
    setSkip(0);
  }, []);

  const handleApiSave = useCallback(
    async (updatedApi) => {
      try {
        if (isAddingApi) {
          const apiCallData = {
            ...updatedApi,
            status_id: selected.status_id,
          };
          const newApiCall =
            await workflowStatusService.createWorkflowApiCall(apiCallData);
          setApiConfigs((prev) => [...prev, newApiCall]);
          setIsAddingApi(false);
          toast.success("API-Konfiguration erfolgreich erstellt!");
        } else {
          const apiCallData = {
            ...updatedApi,
            status_id: selected.status_id,
          };
          const updatedApiCall =
            await workflowStatusService.updateWorkflowApiCall(
              updatedApi.api_call_id,
              apiCallData,
            );
          setApiConfigs((prev) =>
            prev.map((api) =>
              api.api_call_id === updatedApi.api_call_id ? updatedApiCall : api,
            ),
          );
          toast.success("API-Konfiguration erfolgreich aktualisiert!");
        }
        setEditingApi(null);
      } catch (error) {
        console.error("Error saving API call:", error);
        toast.error(
          "Fehler beim Speichern der API-Konfiguration. Bitte versuchen Sie es erneut.",
        );
        throw error;
      }
    },
    [selected?.status_id, isAddingApi],
  );

  const handleApiCancel = () => {
    setEditingApi(null);
  };

  const handleApiChange = (field, value) => {
    setEditingApi((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewApiChange = (field, value) => {
    setNewApi((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeleteApi = useCallback(
    async (id) => {
      try {
        const apiToDelete = apiConfigs.find((api) => api.api_call_id === id);
        const apiName = apiToDelete?.call_name || "API-Konfiguration";
        await workflowStatusService.deleteWorkflowApiCall(id);
        setApiConfigs((prev) => prev.filter((api) => api.api_call_id !== id));
        toast.success(`API "${apiName}" wurde erfolgreich gelöscht!`);
      } catch (error) {
        console.error("Error deleting API call:", error);
        toast.error(
          "Fehler beim Löschen der API-Konfiguration. Bitte versuchen Sie es erneut.",
        );
      }
    },
    [selected?.status_id, apiConfigs],
  );

  const handleCancelAddApi = () => {
    setIsAddingApi(false);
    setNewApi({
      call_name: "",
      endpoint_url: "",
      http_method: "POST",
      headers: {},
      payload_template: {},
      timeout_seconds: 30,
      retry_attempts: 3,
      is_enabled: true,
      execution_order: 0,
      is_blocking: false,
    });
  };

  const handleEmailSave = useCallback(
    async (updatedEmail) => {
      try {
        if (isAddingEmail) {
          const emailNotificationData = {
            ...updatedEmail,
            status_id: selected.status_id,
          };
          const newEmailNotification =
            await workflowStatusService.createWorkflowEmailNotification(
              emailNotificationData,
            );
          setEmailConfigs((prev) => [...prev, newEmailNotification]);
          setIsAddingEmail(false);
          toast.success("E-Mail-Benachrichtigung erfolgreich erstellt!");
        } else {
          const emailNotificationData = {
            ...updatedEmail,
            status_id: selected.status_id,
          };
          const updatedEmailNotification =
            await workflowStatusService.updateWorkflowEmailNotification(
              updatedEmail.notification_id,
              emailNotificationData,
            );
          setEmailConfigs((prev) =>
            prev.map((email) =>
              email.notification_id === updatedEmail.notification_id
                ? updatedEmailNotification
                : email,
            ),
          );
          toast.success("E-Mail-Benachrichtigung erfolgreich aktualisiert!");
        }
        setEditingEmail(null);
      } catch (error) {
        console.error("Error saving email notification:", error);
        toast.error(
          "Fehler beim Speichern der E-Mail-Benachrichtigung. Bitte versuchen Sie es erneut.",
        );
        throw error;
      }
    },
    [selected?.status_id, isAddingEmail],
  );

  const handleEmailCancel = () => {
    setEditingEmail(null);
  };

  const handleDeleteClick = (status) => {
    setEditingStatus(status);
    setIsDeleteFormOpen(true);
  };

  const handleDeleteStatus = async () => {
    try {
      await workflowStatusService.deleteWorkflowStatus(editingStatus.status_id);
      setStatuses((prevStatuses) =>
        prevStatuses.filter(
          (status) => status.status_id !== editingStatus.status_id,
        ),
      );
      if (selected && selected.status_id === editingStatus.status_id) {
        setSelected(null);
        setActiveTab("overview");
      }
      setIsDeleteFormOpen(false);
      setEditingStatus(null);
      toast.success("Workflow status deleted successfully!");
    } catch (error) {
      console.error("Error deleting workflow status:", error);
      toast.error("Failed to delete workflow status");
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteFormOpen(false);
    setEditingStatus(null);
  };

  const handleEmailChange = (field, value) => {
    setEditingEmail((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewEmailChange = (field, value) => {
    setNewEmail((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancelAddEmail = () => {
    setIsAddingEmail(false);
    setNewEmail({
      notification_name: "",
      template_code: "",
      recipient_type: "customer",
      custom_recipients: [],
      delay_hours: 0,
      is_enabled: true,
      send_immediately: true,
      execution_order: 0,
      conditions: {},
    });
  };

  const handleTemplateSave = useCallback(
    async (templateData) => {
      try {
        if (editingTemplate) {
          if (
            editingEmail?.notification_id === editingTemplate.notification_id
          ) {
            setEditingEmail((prev) => ({
              ...prev,
              templateContent: templateData.body_html,
              template_code:
                templateData.template_code || editingTemplate.template_code,
            }));
          } else if (isAddingEmail) {
            setNewEmail((prev) => ({
              ...prev,
              templateContent: templateData.body_html,
              template_code:
                templateData.template_code || editingTemplate.template_code,
            }));
          }
        }

        if (templateData.template_id) {
          setTemplates((prevTemplates) =>
            prevTemplates.map((template) =>
              template.template_id === templateData.template_id
                ? { ...template, ...templateData }
                : template,
            ),
          );
        }

        setEditingTemplate((prev) => ({
          ...prev,
          templateContent: templateData.body_html,
          template_code: templateData.template_code || prev.template_code,
          template_name: templateData.template_name || prev.template_name,
          subject: templateData.subject || prev.subject,
          is_active:
            templateData.is_active !== undefined
              ? templateData.is_active
              : prev.is_active,
        }));

        if (templateData.templateStatusChanged && selected) {
          try {
            const updatedEmailConfigs =
              await workflowStatusService.getWorkflowEmailNotifications(
                selected.status_id,
              );
            setEmailConfigs(updatedEmailConfigs.items || []);

            if (editingEmail) {
              const updatedEmail = updatedEmailConfigs.items?.find(
                (item) => item.notification_id === editingEmail.notification_id,
              );
              if (updatedEmail) {
                setEditingEmail(updatedEmail);
              }
            }
          } catch (error) {
            console.error("Error refreshing workflow data:", error);
          }
        }

        if (templateData.notificationsUpdated > 0) {
          showSuccess(
            `Template und ${templateData.notificationsUpdated} E-Mail-Benachrichtigungen aktualisiert!`,
          );
        } else {
          showSuccess("Template erfolgreich gespeichert!");
        }

        setTimeout(() => {
          setEditingTemplate(null);
        }, 2000);
      } catch (error) {
        console.error("Error saving template:", error);
        showError(
          "Fehler beim Speichern des Templates. Bitte versuchen Sie es erneut.",
        );
      }
    },
    [
      editingTemplate,
      editingEmail,
      isAddingEmail,
      selected,
      showSuccess,
      showError,
    ],
  );

  const handleTemplateCancel = useCallback(() => {
    setEditingTemplate(null);
  }, []);

  const handleTemplateChange = useCallback((content) => {
    setEditingTemplate((prev) => ({
      ...prev,
      templateContent: content,
    }));
  }, []);

  const handleDeleteEmail = useCallback(
    async (email) => {
      try {
        const emailName = email.notification_name || "E-Mail-Benachrichtigung";
        await workflowStatusService.deleteWorkflowEmailNotification(
          email.notification_id,
        );
        setEmailConfigs((prev) =>
          prev.filter((e) => e.notification_id !== email.notification_id),
        );
        toast.success(
          `E-Mail-Benachrichtigung "${emailName}" wurde erfolgreich gelöscht!`,
        );
      } catch (error) {
        console.error("Error deleting email notification:", error);
        toast.error(
          "Fehler beim Löschen der E-Mail-Benachrichtigung. Bitte versuchen Sie es erneut.",
        );
      }
    },
    [selected?.status_id],
  );

  const ApiCallsTab = useCallback(
    () => (
      <div className="md:space-y-6 space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            API-Aufrufe
          </h3>
          {hasPermission("api.manage") && (
            <button
              onClick={() => {
                // Ensure no other editor is open when adding a new API
                setEditingApi(null);
                setIsAddingApi(true);
                setEditingEmail(null);
                setIsAddingEmail(false);
                setEditingTemplate(null);
              }}
              className="inline-flex items-center px-3 py-2 text-sm font-medium primary-color bg-primary-color-tint border border-primary-color rounded-lg hover:opacity-90 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              API hinzufügen
            </button>
          )}
        </div>

        {apiConfigsLoading ? (
          <LoadingOverlay message="Lade API-Konfigurationen..." />
        ) : (
          <div className="space-y-4">
            {isAddingApi && (
              <ApiConfigurationForm
                api={newApi}
                isNew={true}
                onSave={handleApiSave}
                onCancel={handleCancelAddApi}
                onChange={handleNewApiChange}
                onDelete={handleDeleteApi}
                canManage={hasPermission("api.manage")}
              />
            )}

            {apiConfigs.map((api) => (
              <div key={api.api_call_id}>
                {editingApi?.api_call_id === api.api_call_id ? (
                  <ApiConfigurationForm
                    api={editingApi}
                    isNew={false}
                    onSave={handleApiSave}
                    onCancel={handleApiCancel}
                    onChange={handleApiChange}
                    onDelete={handleDeleteApi}
                    canManage={hasPermission("api.manage")}
                  />
                ) : (
                  <ApiConfigurationCard
                    api={api}
                    onEdit={(item) => {
                      // Open only one editor at a time
                      setIsAddingApi(false);
                      setEditingEmail(null);
                      setIsAddingEmail(false);
                      setEditingTemplate(null);
                      setEditingApi(item);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    [
      isAddingApi,
      newApi,
      handleApiSave,
      handleCancelAddApi,
      handleNewApiChange,
      apiConfigs,
      editingApi,
      handleApiCancel,
      handleApiChange,
      setEditingApi,
      apiConfigsLoading,
      handleDeleteApi,
      hasPermission,
    ],
  );

  const EmailNotificationsTab = useCallback(
    () => (
      <div className="md:space-y-6 space-y-4">
        <div className="flex flex-col md:flex-row justify-between md:items-center items-start gap-2 sm:gap-0">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            E-Mail-Benachrichtigungen
          </h3>
          <button
            onClick={() => {
              // Ensure no other editor is open when adding a new Email
              setEditingEmail(null);
              setIsAddingEmail(true);
              setEditingApi(null);
              setIsAddingApi(false);
              setEditingTemplate(null);
            }}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            E-Mail hinzufügen
          </button>
        </div>

        {emailConfigsLoading ? (
          <LoadingOverlay message="Lade E-Mail-Konfigurationen..." />
        ) : (
          <div className="space-y-4">
            {isAddingEmail && (
              <EmailConfigurationForm
                key="new-email-form"
                email={newEmail}
                isNew={true}
                onSave={handleEmailSave}
                onCancel={handleCancelAddEmail}
                onChange={handleNewEmailChange}
                onOpenTemplateEditor={handleOpenTemplateEditor}
              />
            )}

            {emailConfigs.map((email) => (
              <div key={email.notification_id}>
                {editingEmail?.notification_id === email.notification_id ? (
                  <EmailConfigurationForm
                    key={`edit-email-${email.notification_id}`}
                    email={editingEmail}
                    isNew={false}
                    onSave={handleEmailSave}
                    onCancel={handleEmailCancel}
                    onChange={handleEmailChange}
                    onDelete={handleDeleteEmail}
                    onOpenTemplateEditor={handleOpenTemplateEditor}
                  />
                ) : (
                  <EmailConfigurationCard
                    email={email}
                    onEdit={(item) => {
                      // Open only one editor at a time
                      setIsAddingEmail(false);
                      setEditingApi(null);
                      setIsAddingApi(false);
                      setEditingTemplate(null);
                      setEditingEmail(item);
                    }}
                    onDelete={handleDeleteEmail}
                    onOpenTemplateEditor={handleOpenTemplateEditor}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    [
      isAddingEmail,
      newEmail,
      handleCancelAddEmail,
      handleNewEmailChange,
      emailConfigs,
      editingEmail,
      handleEmailSave,
      handleEmailCancel,
      handleEmailChange,
      setEditingEmail,
      emailConfigsLoading,
      handleDeleteEmail,
    ],
  );

  const handleEditClick = (status) => {
    setEditingStatus(status);
    setIsEditFormOpen(true);
  };

  const handleSaveStatus = async (formData) => {
    try {
      const updatedStatus = await workflowStatusService.updateWorkflowStatus(
        editingStatus.status_id,
        formData,
      );
      setStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.status_id === editingStatus.status_id
            ? { ...status, ...updatedStatus }
            : status,
        ),
      );
      if (selected && selected.status_id === editingStatus.status_id) {
        setSelected({ ...selected, ...updatedStatus });
      }

      toast.success("Workflow status updated successfully!");
    } catch (error) {
      console.error("Error updating workflow status:", error);
      toast.error("Failed to update workflow status");
      throw error;
    }
  };

  const handleAddClick = () => {
    setIsAddFormOpen(true);
  };

  // Add new handler for creating new status
  const handleCreateStatus = async (formData) => {
    try {
      const newStatus =
        await workflowStatusService.createWorkflowStatus(formData);
      const statusWithCounts = {
        ...newStatus,
        apiCount: 0,
        emailCount: 0,
        actionCount: 0,
      };
      setStatuses((prevStatuses) => [...prevStatuses, statusWithCounts]);
      setSelected(statusWithCounts);
      setActiveTab("overview");
      toast.success("Workflow status created successfully!");
    } catch (error) {
      console.error("Error creating workflow status:", error);
      toast.error("Failed to create workflow status");
      throw error;
    }
  };

  // Add new state for export loading
  const [isExporting, setIsExporting] = useState(false);

  const handleExportConfig = async () => {
    setIsExporting(true);
    try {
      const blob = await workflowStatusService.exportWorkflowConfig();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const date = new Date().toISOString().split("T")[0];
      link.download = `workflow-config-${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Workflow configuration exported successfully!");
    } catch (error) {
      console.error("Error exporting workflow config:", error);
      toast.error("Failed to export workflow configuration");
    } finally {
      setIsExporting(false);
    }
  };

  const { companyName } = useTenantBranding();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title={companyName || "Workflow Dashboard"}
        subtitle="Workflow-Konfiguration (Datenbank-integriert)"
        buttons={[
          {
            text: isExporting ? "Exporting..." : "Export Konfiguration",
            icon: isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Database className="w-4 h-4" />
            ),
            onClick: handleExportConfig,
            disabled: isExporting,
            className:
              "inline-flex items-center md:px-4 px-3 md:py-3 py-2 text-sm font-semibold rounded-xl text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:text-slate-400 dark:disabled:text-slate-500 disabled:cursor-not-allowed transition-colors duration-200",
          },
          {
            text: "Neuer Status",
            icon: <Plus className="w-4 h-4" />,
            onClick: handleAddClick,
            className:
              "inline-flex items-center md:px-6 px-4 md:py-3 py-2 text-sm font-semibold rounded-xl text-white bg-primary-color hover:opacity-90 transition-colors duration-200 shadow-lg",
          },
        ]}
      />

      <div className="mx-auto px-4 lg:px-4 py-4">
        <InlineNotification
          notification={notification}
          onClose={hideNotification}
        />

        {!loading && statuses.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-10 text-center">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              Keine Workflow-Status gefunden
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Es sind noch keine Workflow-Status konfiguriert. Klicken Sie auf{" "}
              <span className="font-semibold">„Neuer Status“</span>, um einen
              ersten Status anzulegen.
            </p>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row xl:gap-4 gap-4">
            {(!isMobile || !selected) && (
              <div
                className={`${loading ? "w-full" : "md:w-1/3 w-full md:min-w-[260px]"}`}
              >
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 md:p-6 p-4">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 md:mb-6 mb-4">
                    Status-Übersicht ({total})
                  </h2>
                  <div className="space-y-4">
                    {loading ? (
                      <LoadingOverlay loading={loading} />
                    ) : (
                      statuses.map((status) => (
                        <StatusCard
                          key={status.status_id}
                          status={status}
                          isSelected={selected?.status_id === status.status_id}
                          onClick={() => {
                            handleStatusSelect(status);
                          }}
                          colorMap={colorMap}
                          apiCount={status.apiCount || 0}
                          emailCount={status.emailCount || 0}
                          actionCount={status.actionCount || 0}
                        />
                      ))
                    )}
                  </div>
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={limit}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              </div>
            )}

            {(selected || !isMobile) && (
              <div
                className={`${loading ? "hidden" : "opacity-100"}`}
                style={{ width: isMobile ? "100%" : "calc(100% - 276px)" }}
              >
                {selected ? (
                  <div
                    className="bg-white dark:bg-slate-800 shadow rounded-lg sticky top-6 md:max-h-[96vh] md:overflow-y-auto dark:border dark:border-slate-700"
                    style={{ scrollbarWidth: "thin" }}
                  >
                    <div className="md:p-6 p-4 border-b border-slate-200 dark:border-slate-700">
                      <div className="flex justify-between items-start">
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                            {selected.status_name}
                          </h2>
                          <p className="text-slate-600 dark:text-slate-400 mt-1">
                            {selected.description}
                          </p>
                          <div className="flex flex-col md:flex-row items-start md:items-center sm:gap-4 gap-2 mt-3">
                            <span
                              className={`px-3 py-1 rounded-lg text-sm font-medium border ${colorMap[selected.color_code] || "bg-slate-100 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-600"}`}
                            >
                              {selected.color_code}
                            </span>
                            <span className="text-sm text-slate-500 dark:text-slate-400 font-mono">
                              ID: {selected.status_id}
                            </span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              Code: {selected.status_code}
                            </span>
                          </div>
                        </div>
                        <div className="flex md:gap-2 gap-0">
                          <button
                            onClick={() => handleEditClick(selected)}
                            className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                          >
                            <SquarePen className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(selected)}
                            className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                          >
                            <Trash2
                              className="w-4 h-4"
                              role="button"
                              color="red"
                            />
                          </button>
                          {isMobile && (
                            <button
                              onClick={() => handleStatusSelect(null)}
                              className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="md:px-6 px-4 md:pt-6 pt-4">
                      <div className="flex space-x-1 bg-slate-100 dark:bg-slate-700/50 p-1 rounded-lg overflow-x-auto">
                        <button
                          onClick={() => handleTabClick("overview")}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                            activeTab === "overview"
                              ? "bg-white dark:bg-slate-700 primary-color shadow-sm"
                              : "text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                          }`}
                        >
                          <Eye className="w-4 h-4" />
                          Übersicht
                        </button>
                        {hasPermission("api.access") && (
                          <button
                            onClick={() => handleTabClick("api")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                              activeTab === "api"
                                ? "bg-white dark:bg-slate-700 primary-color shadow-sm"
                                : "text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                            }`}
                          >
                            <Globe className="w-4 h-4" />
                            API-Aufrufe
                          </button>
                        )}
                        <button
                          onClick={() => handleTabClick("email")}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                            activeTab === "email"
                              ? "bg-white dark:bg-slate-700 primary-color shadow-sm"
                              : "text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                          }`}
                        >
                          <Mail className="w-4 h-4" />
                          E-Mail-Benachrichtigungen
                        </button>
                      </div>
                    </div>
                    <div className="md:p-6 p-4">
                      {activeTab === "overview" && (
                        <OverviewTab
                          selected={selected}
                          emailNotificationsCount={selected?.emailCount || 0}
                          apiCallsCount={selected?.apiCount || 0}
                        />
                      )}
                      {activeTab === "api" && hasPermission("api.access") && (
                        <ApiCallsTab />
                      )}
                      {activeTab === "email" && <EmailNotificationsTab />}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                    Bitte wählen Sie einen Status aus.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {templateEditorLoading && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-600">
            <LoadingOverlay message="Lade Template-Editor..." />
          </div>
        </div>
      )}

      {editingTemplate && !templateEditorLoading && (
        <EmailTemplateEditor
          key={`template-editor-${editingTemplate.notification_id || "new"}-${Date.now()}`}
          template={editingTemplate.templateContent}
          selectedTemplateCode={editingTemplate.template_code}
          workflowId={selected?.status_id}
          templates={templates}
          templatesLoading={templatesLoading}
          onSave={handleTemplateSave}
          onCancel={handleTemplateCancel}
          onTemplateChange={handleTemplateChange}
        />
      )}

      {isEditFormOpen && editingStatus && (
        <EditStatusForm
          status={editingStatus}
          isOpen={isEditFormOpen}
          onClose={() => {
            setIsEditFormOpen(false);
            setEditingStatus(null);
          }}
          onSave={handleSaveStatus}
          colorMap={colorMap}
        />
      )}

      {isDeleteFormOpen && editingStatus && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md mx-4 dark:border dark:border-slate-700">
            <div className="sm:p-6 p-4">
              <div className="flex flex-col sm:flex-row items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Delete Workflow Status
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mb-6 text-center sm:text-left">
                <p className="text-slate-700 dark:text-slate-300">
                  Are you sure you want to delete the workflow status{" "}
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    &ldquo;{editingStatus.status_name}&rdquo;
                  </span>
                  ?
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  This will also remove all associated API calls and email
                  notifications.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleCancelDelete}
                  className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-600 hover:bg-slate-200 dark:hover:bg-slate-500 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteStatus}
                  className="flex-1 px-4 py-2 bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 rounded-lg font-medium transition-colors"
                >
                  Delete Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAddFormOpen && (
        <AddStatusForm
          isOpen={isAddFormOpen}
          onClose={() => setIsAddFormOpen(false)}
          onSave={handleCreateStatus}
          colorMap={colorMap}
        />
      )}
    </div>
  );
};

export default WorkflowStatus;
